import { modelDialogTools } from "@/utils/globalConfig";
import { RecordingCamera, RecordingEnhancementOptions, OnnxGpuIO, getDefaultOrtWasmPaths } from 'visionary-core';
import { drawDataURLToCanvas, downloadCapturedFramesAsZip, ICapturedFrame, getI18nText } from '@/utils/commonFunc';
import { ExportDebugger } from '../utils/ExportDebugger';

import {
    composeLineDrawingOnCPU,
    readTensorCPUData,
} from './lineDrawingUtils';
import { VideoExportOptions } from "@/utils/sceneUtils";

type OrtModule = typeof import('onnxruntime-web/webgpu');

interface LineDrawingPipeline {
    ort: OrtModule;
    drawingSession: any;
    reliferSession: any;
}

interface FramePostProcessorSetup {
    enhancements: RecordingEnhancementOptions;
    cleanup: () => void;
}

// EXGS 推理 Pipeline
interface ExgsPipeline {
    ort: OrtModule;
    provider: 'webgpu' | 'wasm';
    vaeEncoderSession: any;
    unetSession: any;
    vaeDecoderSession: any;
}

// EXGS 导出选项
interface ExgsPostProcessOptions {
    enableDebugger?: boolean;
    writeToVideo?: boolean;
    collectZip?: boolean;
    zipFileName?: string;
    provider?: 'webgpu' | 'wasm';
    targetResolution?: { width: number; height: number };
}

// 可选风格化导出视频是否逐帧输出调试
interface LineDrawingOptions {
    enableDebugger?: boolean;
    writeToVideo?: boolean;
    collectZip?: boolean;
    zipFileName?: string;
}

export class MediaController {
    private readonly lineDrawingModelBaseUrl: string = '/public/onnx_postprocessing';
    private readonly lineDrawingTensorInputOptions = {
        dataType: 'float32',
        tensorFormat: 'RGB',
        tensorLayout: 'NCHW',
    } as const;

    private lineDrawingPipelinePromise?: Promise<LineDrawingPipeline>;

    // ====== EXGS: 图像修复 / 增强 后处理 ======
    private readonly exgsModelBaseUrl: string = '/public/exgs';
    private readonly exgsDefaultResolution = { width: 1024, height: 576 };

    private exgsPipelinePromise?: Promise<ExgsPipeline>;

    constructor() {

    }

    async ensureLineDrawingPipeline(silent?: boolean): Promise<LineDrawingPipeline> {
        if (!this.lineDrawingPipelinePromise) {
            this.lineDrawingPipelinePromise = (async () => {
                if (!silent) {
                    modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.loadingLineDrawingModel'));
                }

                // 在创建 session 之前设置 wasm 路径，与当前 HTML 同级的 src/ort/
                const ort = await import('onnxruntime-web/webgpu');
                
                if (ort.env && ort.env.wasm) {
                    ort.env.wasm.wasmPaths = getDefaultOrtWasmPaths();
                    console.log('[VisionaryScene] ONNX Runtime WASM paths configured:', ort.env.wasm.wasmPaths);
                }
                
                const drawingModelUrl = `${this.lineDrawingModelBaseUrl}/line-drawings.onnx`;
                const reliferModelUrl = `${this.lineDrawingModelBaseUrl}/relifer_gpu_ready.onnx`;

                const sessionOptions = { executionProviders: ['webgpu'] };
                let drawingSession;
                try {
                    drawingSession = await ort.InferenceSession.create(drawingModelUrl, sessionOptions);
                } catch (e) {
                    console.warn('WebGPU加载失败，尝试使用Wasm', e);
                    drawingSession = await ort.InferenceSession.create(drawingModelUrl, { executionProviders: ['wasm'] });
                }

                let reliferSession;
                const reliferGpuOptions = {
                    executionProviders: ['webgpu'],
                    preferredOutputLocation: 'gpu-buffer' as const,
                };
                try {
                    reliferSession = await ort.InferenceSession.create(reliferModelUrl, reliferGpuOptions);
                } catch (reliferError) {
                    console.warn('WebGPU 加载增强模型失败，尝试使用 Wasm 回退', reliferError);
                    reliferSession = await ort.InferenceSession.create(reliferModelUrl, { executionProviders: ['wasm'] });
                }

                return { ort, drawingSession, reliferSession };
            })();

            this.lineDrawingPipelinePromise.catch(() => {
                this.lineDrawingPipelinePromise = undefined;
            });
        }

        return this.lineDrawingPipelinePromise;
    }
    

    // 配合resize分辨率版本，可删
    async resetLineDrawingPipeline() {
        if (this.lineDrawingPipelinePromise) {
            console.warn('[FixDebug] 检测到显存压力，正在强制重置 AI 引擎...');
            try {
                const pipeline = await this.lineDrawingPipelinePromise;
                // 释放 Session 资源
                if (pipeline.drawingSession) {
                    try { (pipeline.drawingSession as any).release(); } catch (e) { }
                }
                if (pipeline.reliferSession) {
                    try { (pipeline.reliferSession as any).release(); } catch (e) { }
                }
            } catch (e) {
                console.warn('重置 Pipeline 失败', e);
            }
            // 清空引用，强制下一次重新加载
            this.lineDrawingPipelinePromise = undefined;

            // 给浏览器 GC 一点时间
            await new Promise(r => setTimeout(r, 500));
            console.warn('[FixDebug] AI 引擎已重置，显存应已释放。');
        }
    }

    /**
     * 初始化 EXGS pipeline
     */
    async ensureExgsPipeline(options?: { silent?: boolean; provider?: 'webgpu' | 'wasm' }): Promise<ExgsPipeline> {
        if (!this.exgsPipelinePromise) {
            this.exgsPipelinePromise = (async () => {
                const { silent, provider: prefer = 'webgpu' } = options || {};

                if (!silent) {
                    modelDialogTools.showLoadingInfoModal(getI18nText('loadingMessage.loadingEXGSModel'));
                }

                const ort: OrtModule = await import('onnxruntime-web/webgpu');

                // 让 inference_exgs.js 能直接用到全局 ort
                (globalThis as any).ort = ort;

                // 和线稿一致配置 wasm 路径
                if (ort.env && ort.env.wasm) {
                    ort.env.wasm.wasmPaths = getDefaultOrtWasmPaths();
                }

                let provider: 'webgpu' | 'wasm' = prefer;
                const hasWebGPU = typeof navigator !== 'undefined' && 'gpu' in navigator;
                if (provider === 'webgpu' && !hasWebGPU) {
                    console.warn('[EXGS] 当前环境不支持 WebGPU，自动回退 WASM');
                    provider = 'wasm';
                }

                if (provider === 'wasm' && ort.env?.wasm) {
                    ort.env.wasm.numThreads = 2;
                    ort.env.wasm.simd = true;
                }

                (globalThis as any).__exgsRuntimeConfig = {
                    provider,
                    threads: provider === 'wasm' ? (ort.env?.wasm?.numThreads ?? 1) : 1,
                };

                const sessOpt: any = {
                    executionProviders: [provider],
                    enableMemPattern: false,
                    enableCpuMemArena: false,
                    extra: {
                        session: {
                            disable_prepacking: '1',
                            use_device_allocator_for_initializers: '1',
                            use_ort_model_bytes_directly: '1',
                            use_ort_model_bytes_for_initializers: '1',
                        },
                    },
                };

                const vaeEncoderUrl = `${this.exgsModelBaseUrl}/vae_encoder.onnx`;
                const vaeDecoderUrl = `${this.exgsModelBaseUrl}/vae_decoder.onnx`;
                const unetUrl = `${this.exgsModelBaseUrl}/unet_no_ln.onnx`;

                const [vaeEncBuf, vaeDecBuf] = await Promise.all([
                    fetch(vaeEncoderUrl).then(r => r.arrayBuffer()),
                    fetch(vaeDecoderUrl).then(r => r.arrayBuffer()),
                ]);

                const vaeEncoderSession = await ort.InferenceSession.create(vaeEncBuf, sessOpt);
                const vaeDecoderSession = await ort.InferenceSession.create(vaeDecBuf, sessOpt);

                let unetSession: any;
                try {
                    unetSession = await ort.InferenceSession.create(unetUrl, sessOpt);
                } catch (e) {
                    console.warn('[EXGS] WebGPU 加载 UNet 失败，尝试回退 WASM', e);
                    const wasmOpt = { ...sessOpt, executionProviders: ['wasm'] as const };
                    unetSession = await ort.InferenceSession.create(unetUrl, wasmOpt);
                    provider = 'wasm';
                    (globalThis as any).__exgsRuntimeConfig = {
                        provider,
                        threads: ort.env?.wasm?.numThreads ?? 1,
                    };
                }

                const g = globalThis as any;
                if (!g.models) g.models = {};
                g.models.vae_encoder = { sess: vaeEncoderSession };
                g.models.vae_decoder = { sess: vaeDecoderSession };
                g.models.unet = { sess: unetSession };

                console.log('[EXGS] pipeline 初始化完成, provider =', provider);

                return {
                    ort,
                    provider,
                    vaeEncoderSession,
                    unetSession,
                    vaeDecoderSession,
                };
            })();

            this.exgsPipelinePromise.catch(() => {
                this.exgsPipelinePromise = undefined;
            });
        }

        return this.exgsPipelinePromise;
    }

    async resetExgsPipeline() {
        if (!this.exgsPipelinePromise) return;

        console.warn('[EXGS] 检测到显存压力 / 异常，正在重置 EXGS pipeline...');
        try {
            const pipeline = await this.exgsPipelinePromise;
            try { (pipeline.vaeEncoderSession as any)?.release?.(); } catch (e) { console.warn('[EXGS] 释放 vaeEncoderSession 失败', e); }
            try { (pipeline.unetSession as any)?.release?.(); } catch (e) { console.warn('[EXGS] 释放 unetSession 失败', e); }
            try { (pipeline.vaeDecoderSession as any)?.release?.(); } catch (e) { console.warn('[EXGS] 释放 vaeDecoderSession 失败', e); }
        } catch (e) {
            console.warn('[EXGS] 重置 pipeline 失败', e);
        }

        this.exgsPipelinePromise = undefined;
        await new Promise(r => setTimeout(r, 500));
        console.warn('[EXGS] EXGS pipeline 已重置完成');
    }

    /**
     * 使用 EXGS 做图像→图像处理
     */
    async convertFrameWithExgs(
        sourceCanvas: HTMLCanvasElement,
        options?: { silent?: boolean; provider?: 'webgpu' | 'wasm'; targetResolution?: { width: number; height: number } }
    ): Promise<string> {
        const TAG = '[EXGS]';
        const tStart = performance.now();
        let tLast = tStart;
        let needReset = false;

        const logStep = (msg: string) => {
            const now = performance.now();
            console.log(`${TAG} [总:${(now - tStart).toFixed(0)}ms] [Step:${(now - tLast).toFixed(2)}ms] >>> ${msg}`);
            tLast = now;
        };

        try {
            const pipeline = await this.ensureExgsPipeline(options);
            logStep('Pipeline Ready');

            const ort: OrtModule = pipeline.ort;
            const device: GPUDevice | undefined = (ort as any)?.env?.webgpu?.device;
            if (device?.queue) {
                await device.queue.onSubmittedWorkDone();
            }

            const width = sourceCanvas.width;
            const height = sourceCanvas.height;
            const targetResolution = options?.targetResolution || this.exgsDefaultResolution;
            const inferenceWidth = targetResolution?.width && targetResolution.width > 0 ? Math.round(targetResolution.width) : width;
            const inferenceHeight = targetResolution?.height && targetResolution.height > 0 ? Math.round(targetResolution.height) : height;

            let inferenceCanvas: HTMLCanvasElement = sourceCanvas;
            let downscaleCanvas: HTMLCanvasElement | null = null;
            const shouldDownscale = (inferenceWidth < width) || (inferenceHeight < height);
            if (shouldDownscale) {
                downscaleCanvas = document.createElement('canvas');
                downscaleCanvas.width = inferenceWidth;
                downscaleCanvas.height = inferenceHeight;
                const downscaleCtx = downscaleCanvas.getContext('2d');
                if (!downscaleCtx) {
                    throw new Error('无法创建 EXGS 输入降级画布');
                }
                downscaleCtx.drawImage(sourceCanvas, 0, 0, inferenceWidth, inferenceHeight);
                inferenceCanvas = downscaleCanvas;
                logStep(`降级: ${width}x${height} -> ${inferenceWidth}x${inferenceHeight}`);
            }

            const dataURL = await OnnxGpuIO.runExclusive(async () => {
                const encoderInputName = this.getPrimaryInputName(pipeline.vaeEncoderSession, 'x_input');
                const encoderNeedsFloat16 = this.sessionInputRequiresFloat16(pipeline.vaeEncoderSession, encoderInputName);
                const encoderInput = this.createInputTensorFromCanvas(inferenceCanvas, inferenceWidth, inferenceHeight, encoderNeedsFloat16, ort);

                const encoderResult = await pipeline.vaeEncoderSession.run({ [encoderInputName]: encoderInput });
                const encoderOutputName = this.getPrimaryOutputName(pipeline.vaeEncoderSession, encoderResult);
                const latentTensor = encoderResult[encoderOutputName];
                if (!latentTensor) throw new Error('VAE Encoder 输出为空');

                const latentFloat32 = this.tensorDataToFloat32(latentTensor);
                const latentDims = latentTensor.dims;

                const unetInputName = this.getPrimaryInputName(pipeline.unetSession, 'latent');
                const unetNeedsFloat16 = this.sessionInputRequiresFloat16(pipeline.unetSession, unetInputName, true);
                const unetInputTensor = unetNeedsFloat16
                    ? this.createFloat16TensorFromFloat32(latentFloat32, latentDims, ort)
                    : new ort.Tensor(latentFloat32, latentDims);

                console.log(`[EXGS] 即将执行 UNet，输入 ${unetInputName}: ${this.describeTensor(unetInputTensor)}`);
                const unetResult = await pipeline.unetSession.run({ [unetInputName]: unetInputTensor });
                const unetOutputName = this.getPrimaryOutputName(pipeline.unetSession, unetResult);
                const noiseTensor = unetResult[unetOutputName];
                if (!noiseTensor) throw new Error('UNet 输出为空');

                const noiseFloat32 = this.tensorDataToFloat32(noiseTensor);
                if (noiseFloat32.length !== latentFloat32.length) {
                    throw new Error('UNet 输出与 Latent 尺寸不匹配');
                }

                const denoisedLatent = this.denoiseLatent(latentFloat32, noiseFloat32);
                const decoderInputName = this.getPrimaryInputName(pipeline.vaeDecoderSession, 'latent');
                const decoderFeeds = { [decoderInputName]: new ort.Tensor(denoisedLatent, latentDims) };
                const decoderResult = await pipeline.vaeDecoderSession.run(decoderFeeds);
                const decoderOutputName = this.getPrimaryOutputName(pipeline.vaeDecoderSession, decoderResult);
                const outputTensor = decoderResult[decoderOutputName];
                if (!outputTensor) throw new Error('VAE Decoder 输出为空');

                const outputCanvas = this.tensorToCanvas(outputTensor);

                let finalCanvas = outputCanvas;
                if (shouldDownscale) {
                    const restoreCanvas = document.createElement('canvas');
                    restoreCanvas.width = width;
                    restoreCanvas.height = height;
                    const restoreCtx = restoreCanvas.getContext('2d');
                    if (!restoreCtx) {
                        throw new Error('无法恢复 EXGS 输出分辨率');
                    }
                    restoreCtx.drawImage(outputCanvas, 0, 0, width, height);
                    finalCanvas = restoreCanvas;
                }

                return finalCanvas.toDataURL('image/png');
            });

            logStep('EXGS 推理结束');
            return dataURL;
        } catch (error) {
            console.error(TAG, 'convertFrameWithExgs 出错', error);
            needReset = true;
            throw error;
        } finally {
            const totalTime = performance.now() - tStart;
            if (totalTime > 300000) {
                console.warn(`${TAG} 本帧耗时 ${totalTime.toFixed(0)}ms > 阈值，触发自动重置`);
                needReset = true;
            }

            if (needReset) {
                await this.resetExgsPipeline();
            } else {
                await new Promise(r => setTimeout(r, 100));
            }
        }
    }

    private getPrimaryInputName(session: any, fallback: string): string {
        if (session?.inputNames?.length) {
            return session.inputNames[0];
        }
        return fallback;
    }

    private getPrimaryOutputName(session: any, outputs: Record<string, any>): string {
        if (session?.outputNames?.length) {
            return session.outputNames[0];
        }
        const keys = Object.keys(outputs || {});
        return keys[0];
    }

    private sessionInputRequiresFloat16(session: any, inputName?: string, defaultValue: boolean = false): boolean {
        try {
            const metadata = session?.inputMetadata;
            if (!metadata) return defaultValue;
            if (inputName && metadata[inputName]) {
                return this.isFloat16Type(metadata[inputName]?.type);
            }
            return Object.values(metadata).some(meta => this.isFloat16Type((meta as any)?.type));
        } catch {
            return defaultValue;
        }
    }

    private isFloat16Type(type?: string): boolean {
        if (!type) return false;
        const normalized = type.toLowerCase();
        return normalized.includes('float16');
    }

    private createInputTensorFromCanvas(
        canvas: HTMLCanvasElement,
        targetWidth: number,
        targetHeight: number,
        useFloat16: boolean,
        ort: OrtModule
    ) {
        if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
            const resizeCanvas = document.createElement('canvas');
            resizeCanvas.width = targetWidth;
            resizeCanvas.height = targetHeight;
            const resizeCtx = resizeCanvas.getContext('2d');
            resizeCtx?.drawImage(canvas, 0, 0, targetWidth, targetHeight);
            canvas = resizeCanvas;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('无法获取 Canvas 上下文');
        const imageData = ctx.getImageData(0, 0, targetWidth, targetHeight);
        const { data } = imageData;

        const tensorData = new Float32Array(3 * targetWidth * targetHeight);
        for (let i = 0; i < targetHeight; i++) {
            for (let j = 0; j < targetWidth; j++) {
                const pixelOffset = (i * targetWidth + j) * 4;
                const r = data[pixelOffset] / 255;
                const g = data[pixelOffset + 1] / 255;
                const b = data[pixelOffset + 2] / 255;

                const rNorm = r * 2 - 1;
                const gNorm = g * 2 - 1;
                const bNorm = b * 2 - 1;

                const base = i * targetWidth + j;
                tensorData[base] = rNorm;
                tensorData[targetWidth * targetHeight + base] = gNorm;
                tensorData[2 * targetWidth * targetHeight + base] = bNorm;
            }
        }

        if (useFloat16) {
            const float16Data = this.float32ToFloat16(tensorData);
            return new ort.Tensor('float16', float16Data, [1, 3, targetHeight, targetWidth]);
        }

        return new ort.Tensor(tensorData, [1, 3, targetHeight, targetWidth]);
    }

    private tensorDataToFloat32(tensor: any): Float32Array {
        const data = tensor?.data;
        if (!data) {
            throw new Error('Tensor 数据不存在');
        }
    
        // 已经是 Float32Array，直接用
        if (data instanceof Float32Array) return data;
    
        // ✅ 新增：兼容 Float16Array（Chrome / 新版 JS 环境）
        const F16Array = (globalThis as any).Float16Array;
        if (F16Array && data instanceof F16Array) {
            // Float16Array 的每个元素已经是 JS number，直接拷成 Float32Array 即可
            return Float32Array.from(data as any);
        }
    
        // 其它常见 TypedArray → 直接 cast
        if (data instanceof Float64Array) return Float32Array.from(data);
        if (data instanceof Int32Array)   return Float32Array.from(data);
        if (data instanceof Uint32Array)  return Float32Array.from(data);
        if (data instanceof Int16Array)   return Float32Array.from(data);
        if (data instanceof Int8Array)    return Float32Array.from(data);
        if (data instanceof Uint8Array)   return Float32Array.from(data);
    
        // 旧的 half-buffer（bit-pattern） 分支保留：
        if (data instanceof Uint16Array) {
            return this.float16ToFloat32(data);
        }
    
        // 普通 JS 数组
        if (Array.isArray(data)) return Float32Array.from(data);
    
        // 可选：如果你担心还有 BigInt64Array 之类，也可以加一层：
        if (typeof BigInt64Array !== 'undefined' && data instanceof BigInt64Array) {
            const out = new Float32Array(data.length);
            for (let i = 0; i < data.length; i++) out[i] = Number(data[i]);
            return out;
        }
        if (typeof BigUint64Array !== 'undefined' && data instanceof BigUint64Array) {
            const out = new Float32Array(data.length);
            for (let i = 0; i < data.length; i++) out[i] = Number(data[i]);
            return out;
        }
    
        // 最后一层：把真实类型打出来，方便后续调
        console.error('[EXGS] 未知 Tensor.data 类型: ', (data as any)?.constructor?.name, data);
        throw new Error('无法解析 Tensor 数据类型');
    }
    

    private createFloat16TensorFromFloat32(data: Float32Array, dims: readonly number[], ort: OrtModule) {
        const float16Data = this.float32ToFloat16(data);
        return new ort.Tensor('float16', float16Data, dims);
    }

    private describeTensor(tensor: any): string {
        if (!tensor) return 'null tensor';
        const type = tensor.type || tensor.data?.constructor?.name || typeof tensor;
        const dims = JSON.stringify(tensor.dims || []);
        const length = tensor.data?.length ?? tensor.length ?? 'unknown';
        return `type=${type}, dims=${dims}, length=${length}`;
    }

    private float32ToFloat16(source: Float32Array): Uint16Array {
        const target = new Uint16Array(source.length);
        for (let i = 0; i < source.length; i++) {
            const value = source[i];
            if (!Number.isFinite(value)) {
                target[i] = value > 0 ? 0x7C00 : 0xFC00;
                continue;
            }
            const sign = value < 0 ? 1 : 0;
            const abs = Math.abs(value);
            if (abs === 0) {
                target[i] = sign << 15;
                continue;
            }
            let exponent = Math.floor(Math.log2(abs));
            let mantissa = abs / Math.pow(2, exponent) - 1;
            exponent += 15;
            if (exponent <= 0) {
                target[i] = sign << 15;
                continue;
            }
            if (exponent >= 0x1F) {
                target[i] = (sign << 15) | 0x7C00;
                continue;
            }
            const mantissaBits = Math.round(mantissa * 1024);
            target[i] = (sign << 15) | (exponent << 10) | (mantissaBits & 0x3FF);
        }
        return target;
    }

    private float16ToFloat32(source: Uint16Array): Float32Array {
        const target = new Float32Array(source.length);
        for (let i = 0; i < source.length; i++) {
            const bits = source[i];
            const sign = (bits & 0x8000) ? -1 : 1;
            const exponent = (bits >> 10) & 0x1F;
            const fraction = bits & 0x3FF;
            if (exponent === 0x1F) {
                target[i] = fraction ? NaN : sign * Infinity;
            } else if (exponent === 0) {
                target[i] = sign * Math.pow(2, -14) * (fraction / 1024);
            } else {
                target[i] = sign * Math.pow(2, exponent - 15) * (1 + fraction / 1024);
            }
        }
        return target;
    }

    private denoiseLatent(latent: Float32Array, noise: Float32Array): Float32Array {
        const result = new Float32Array(latent.length);
        for (let i = 0; i < latent.length; i++) {
            result[i] = latent[i] - noise[i];
        }
        return result;
    }

    private tensorToCanvas(tensor: any): HTMLCanvasElement {
        const dims = tensor?.dims;
        if (!dims || dims.length < 4) {
            throw new Error('Decoder 输出维度不正确');
        }
        const height = dims[dims.length - 2];
        const width = dims[dims.length - 1];
        const data = this.tensorDataToFloat32(tensor);

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('无法创建输出画布');
        const imageData = ctx.createImageData(width, height);
        const channelSize = width * height;

        for (let i = 0; i < height; i++) {
            for (let j = 0; j < width; j++) {
                const base = i * width + j;
                let r = (data[base] + 1) * 0.5;
                let g = (data[channelSize + base] + 1) * 0.5;
                let b = (data[2 * channelSize + base] + 1) * 0.5;
                r = Math.min(1, Math.max(0, r));
                g = Math.min(1, Math.max(0, g));
                b = Math.min(1, Math.max(0, b));

                const idx = base * 4;
                imageData.data[idx] = Math.round(r * 255);
                imageData.data[idx + 1] = Math.round(g * 255);
                imageData.data[idx + 2] = Math.round(b * 255);
                imageData.data[idx + 3] = 255;
            }
        }

        ctx.putImageData(imageData, 0, 0);
        return canvas;
    }

    /**
     * 使用 ONNX Runtime 运行线稿提取模型
     */
    //resize分辨率版本
    async convertToLineDrawing(
        sourceCanvas: HTMLCanvasElement,
        options?: { silent?: boolean }
    ): Promise<string> {
        const TAG = '[FinalGPU]';
        const tStart = performance.now();
        let tLast = tStart;

        const logStep = (action: string) => {
            const now = performance.now();
            console.log(`${TAG} [总:${(now - tStart).toFixed(0)}ms] [Step:${(now - tLast).toFixed(2)}ms] >>> ${action}`);
            tLast = now;
        };

        const originalWidth = sourceCanvas.width;
        const originalHeight = sourceCanvas.height;
        const MAX_DIMENSION = 1080;

        let processWidth = originalWidth;
        let processHeight = originalHeight;
        let inputCanvas: HTMLCanvasElement | OffscreenCanvas = sourceCanvas;
        let tempCanvas: HTMLCanvasElement | undefined;
        let ctx: CanvasRenderingContext2D | null = null;
        let imageData: ImageData | null = null;
        let inputTensor: any = null;
        let reliferTensor: any = null;
        let reliferData: any = null;
        let needReset = false; // 标记是否需要重置

        // 1. 降级
        if (originalWidth > MAX_DIMENSION || originalHeight > MAX_DIMENSION) {
            const ratio = Math.min(MAX_DIMENSION / originalWidth, MAX_DIMENSION / originalHeight);
            processWidth = Math.round(originalWidth * ratio);
            processHeight = Math.round(originalHeight * ratio);

            tempCanvas = document.createElement('canvas');
            tempCanvas.width = processWidth;
            tempCanvas.height = processHeight;
            ctx = tempCanvas.getContext('2d');
            if (ctx) {
                ctx.drawImage(sourceCanvas, 0, 0, processWidth, processHeight);
                inputCanvas = tempCanvas;
            }
            logStep(`降级: ${originalWidth}x${originalHeight} -> ${processWidth}x${processHeight}`);
        }

        try {
            // 2. 获取 Pipeline
            const pipeline = await this.ensureLineDrawingPipeline(options?.silent);
            logStep(`Pipeline Ready`);

            // 3. 准备数据
            if (!ctx) ctx = (inputCanvas as HTMLCanvasElement).getContext('2d');
            if (!ctx) throw new Error('Context Lost');

            imageData = ctx.getImageData(0, 0, processWidth, processHeight);

            // 等待 GPU 空闲
            const device = (pipeline.ort as any)?.env?.webgpu?.device as GPUDevice;
            if (device && device.queue) await device.queue.onSubmittedWorkDone();

            inputTensor = await pipeline.ort.Tensor.fromImage(imageData, this.lineDrawingTensorInputOptions);

            // 4. 推理
            const runInference = async () => {
                const tInfer = performance.now();

                const res1 = await pipeline.drawingSession.run({ [pipeline.drawingSession.inputNames[0]]: inputTensor });
                const out1 = res1[pipeline.drawingSession.outputNames[0]];

                if (inputTensor.dispose) inputTensor.dispose();

                const res2 = await pipeline.reliferSession.run({ [pipeline.reliferSession.inputNames[0]]: out1 });
                const out2 = res2[pipeline.reliferSession.outputNames[0]];

                if (out1.dispose) out1.dispose();

                console.log(`${TAG} 推理纯耗时: ${(performance.now() - tInfer).toFixed(2)}ms`);
                return out2;
            };

            reliferTensor = await OnnxGpuIO.runExclusive(runInference);
            logStep(`推理结束`);

            // 5. 回读
            reliferData = await readTensorCPUData(reliferTensor);
            if (reliferTensor.dispose) reliferTensor.dispose();
            logStep(`数据回读`);

            // 6. 合成
            let resultDataURL = composeLineDrawingOnCPU(reliferData, processWidth, processHeight);

            if (tempCanvas) {
                resultDataURL = await this.resizeDataURL(resultDataURL, originalWidth, originalHeight);
            }
            logStep(`完成`);

            return resultDataURL;

        } catch (error) {
            console.error(error);
            needReset = true; // 出错必须重置
            throw error;
        } finally {
            // 清理 JS 对象
            imageData = null;
            inputTensor = null;
            reliferTensor = null;
            reliferData = null;
            const canvasToDispose = tempCanvas;
            if (canvasToDispose) {
                canvasToDispose.width = 1;
                canvasToDispose.height = 1;
            }
            tempCanvas = undefined;

            const totalTime = performance.now() - tStart;

            // =========================================================
            // 自动重置检测
            // 如果本帧耗时超过 3000ms，说明显存已经脏了，下一帧重置
            // =========================================================
            if (totalTime > 300000) {
                console.warn(`${TAG} 本帧耗时 ${totalTime.toFixed(0)}ms > 阈值，触发显存重置策略...`);
                needReset = true;
            }

            if (needReset) {
                await this.resetLineDrawingPipeline();
            } else {
                // 正常帧给一点冷却时间即可
                await new Promise(r => setTimeout(r, 100));
            }
        }
    }

    resizeDataURL(dataURL: string, targetWidth: number, targetHeight: number): Promise<string> {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = targetWidth;
                canvas.height = targetHeight;
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
                    resolve(canvas.toDataURL('image/png'));
                } else {
                    resolve(dataURL);
                }
            };
            img.onerror = () => resolve(dataURL);
            img.src = dataURL;
        });
    }

    async createExgsPostProcessor(
        recordingCamera: RecordingCamera,
        options: ExgsPostProcessOptions = {}
    ): Promise<FramePostProcessorSetup | null> {
        const {
            enableDebugger = false,
            writeToVideo = true,
            collectZip = false,
            zipFileName = 'exgs',
            provider,
            targetResolution = this.exgsDefaultResolution,
        } = options;

        const width = recordingCamera.canvas.width;
        const height = recordingCamera.canvas.height;

        const captureCanvas = writeToVideo ? document.createElement('canvas') : null;
        if (captureCanvas) {
            captureCanvas.width = width;
            captureCanvas.height = height;
        }
        const captureCtx = captureCanvas ? captureCanvas.getContext('2d') : null;
        if (writeToVideo && !captureCtx) {
            throw new Error('无法创建 EXGS 输出画布');
        }

        const capturedFrames: ICapturedFrame[] = [];
        let frameIndex = 0;
        const recordFrameResult = async (finalDataUrl: string): Promise<number> => {
            const currentFrameIdx = frameIndex++;
            if (writeToVideo && captureCanvas && captureCtx) {
                await drawDataURLToCanvas(finalDataUrl, captureCanvas, captureCtx);
            }
            if (collectZip) {
                capturedFrames.push({
                    filename: `exgs_frame_${currentFrameIdx.toString().padStart(5, '0')}.png`,
                    dataUrl: finalDataUrl,
                });
            }
            return currentFrameIdx;
        };

        let processFrame: (inputCanvas: HTMLCanvasElement) => Promise<void>;

        if (enableDebugger) {
            console.log('[VideoExport] 启用 EXGS 调试模式');
            const debuggerUI = ExportDebugger.get();

            processFrame = async (inputCanvas: HTMLCanvasElement) => {
                const tStart = performance.now();
                let logInfo = '';
                let isError = false;

                let snapshotCanvas: HTMLCanvasElement;

                try {
                    snapshotCanvas = inputCanvas;
                } catch (e) {
                    logInfo += `渲染失败: ${(e as Error).message}`;
                    isError = true;
                    snapshotCanvas = document.createElement('canvas');
                    snapshotCanvas.width = width;
                    snapshotCanvas.height = height;
                }

                const isRawBlank = debuggerUI.isCanvasBlank(snapshotCanvas);
                if (isRawBlank) logInfo += '[警告] 原始3D渲染为空! ';

                let finalDataUrl = '';
                try {
                    if (!isError && !isRawBlank) {
                        finalDataUrl = await this.convertFrameWithExgs(snapshotCanvas, {
                            silent: true,
                            provider,
                            targetResolution,
                        });
                    } else {
                        finalDataUrl = snapshotCanvas.toDataURL();
                    }
                } catch (err) {
                    logInfo += `EXGS 异常: ${(err as Error).message}`;
                    isError = true;
                    finalDataUrl = snapshotCanvas.toDataURL();
                }

                const currentFrameIdx = await recordFrameResult(finalDataUrl);
                const duration = (performance.now() - tStart).toFixed(0);
                logInfo += `耗时: ${duration}ms`;
                const isSuspicious = isError || isRawBlank;

                debuggerUI.logFrame(currentFrameIdx, snapshotCanvas, finalDataUrl, logInfo, isSuspicious);
            };
        } else {
            console.log('[VideoExport] 启用 EXGS 模式');

            processFrame = async (inputCanvas: HTMLCanvasElement) => {
                const snapshotCanvas = inputCanvas;

                try {
                    const exgsDataURL = await this.convertFrameWithExgs(snapshotCanvas, {
                        silent: true,
                        provider,
                        targetResolution,
                    });
                    await recordFrameResult(exgsDataURL);
                } catch (error) {
                    console.warn('[VideoExport] EXGS 转换失败，使用原始帧', error);
                    await recordFrameResult(snapshotCanvas.toDataURL());
                }
            };
        }

        return {
            enhancements: {
                ...(writeToVideo && captureCanvas ? { captureCanvas } : {}),
                frameProcessor: processFrame,
            },
            cleanup: () => {
                captureCanvas?.remove();
                if (collectZip && capturedFrames.length) {
                    void downloadCapturedFramesAsZip(capturedFrames, zipFileName);
                }
            },
        };
    }

    async createLineDrawingPostProcessor(
        recordingCamera: RecordingCamera,
        options: LineDrawingOptions = {}
    ): Promise<FramePostProcessorSetup | null> {
    
        // 是否开启调试
        const {
            enableDebugger = false,
            writeToVideo = true,
            collectZip = false,
            zipFileName,
        } = options;
        const shouldStylize = writeToVideo;
    
        const width = recordingCamera.canvas.width;
        const height = recordingCamera.canvas.height;
    
        const captureCanvas = writeToVideo ? document.createElement('canvas') : null;
        if (captureCanvas) {
            captureCanvas.width = width;
            captureCanvas.height = height;
        }
        const captureCtx = captureCanvas ? captureCanvas.getContext('2d') : null;
        if (writeToVideo && !captureCtx) throw new Error('无法创建线稿输出画布');
    
        const capturedFrames: ICapturedFrame[] = [];
        let frameIndex = 0;
        const recordFrameResult = async (finalDataUrl: string): Promise<number> => {
            const currentFrameIdx = frameIndex++;
            if (writeToVideo && captureCanvas && captureCtx) {
                await drawDataURLToCanvas(finalDataUrl, captureCanvas, captureCtx);
            }
            if (collectZip) {
                capturedFrames.push({
                    filename: `line_frame_${currentFrameIdx.toString().padStart(5, '0')}.png`,
                    dataUrl: finalDataUrl,
                });
            }
            return currentFrameIdx;
        };
    
        let processFrame: (inputCanvas: HTMLCanvasElement) => Promise<void>; 
    
        if (enableDebugger) {
            // =========================================================
            // 调试模式：包含所有日志、计时和UI更新
            // =========================================================
            console.log('[VideoExport] 启用深度调试模式');
            const debuggerUI = ExportDebugger.get();
    
            processFrame = async (inputCanvas: HTMLCanvasElement) => {
                const tStart = performance.now();
                let logInfo = "";
                let isError = false;
    
                let snapshotCanvas: HTMLCanvasElement;
    
                try {
                    snapshotCanvas = inputCanvas; 
                } catch (e) {
                    logInfo += `渲染失败: ${(e as Error).message}`;
                    isError = true;
                    snapshotCanvas = document.createElement('canvas'); // 错误时创建一个空白画布
                    snapshotCanvas.width = width;
                    snapshotCanvas.height = height;
                }
    
                const isRawBlank = debuggerUI.isCanvasBlank(snapshotCanvas);
                if (isRawBlank) logInfo += "[警告] 原始3D渲染为空! ";
    
                let finalDataUrl = "";
                if (shouldStylize) {
                    try {
                        if (!isError && !isRawBlank) { // 不在空白或错误的帧上运行AI
                            finalDataUrl = await mediaController.convertToLineDrawing(snapshotCanvas, { silent: true });
                        } else {
                            finalDataUrl = snapshotCanvas.toDataURL();
                        }
                    } catch (error) {
                        logInfo += `AI 异常: ${(error as Error).message}`;
                        isError = true;
                        finalDataUrl = snapshotCanvas.toDataURL();
                    }
                } else {
                    finalDataUrl = snapshotCanvas.toDataURL();
                }
    
                const currentFrameIdx = await recordFrameResult(finalDataUrl);
                const duration = (performance.now() - tStart).toFixed(0);
                logInfo += `耗时: ${duration}ms`;
                const isSuspicious = isError || isRawBlank;
    
                debuggerUI.logFrame(currentFrameIdx, snapshotCanvas, finalDataUrl, logInfo, isSuspicious);
            };
    
        } else {
            // =========================================================
            // 生产模式：精简、高效，只包含核心逻辑
            // =========================================================
            console.log('[VideoExport] 启用线稿模式');
    
            processFrame = async (inputCanvas: HTMLCanvasElement) => {
                // 1. 获取渲染快照
                const snapshotCanvas = inputCanvas;
    
                if (shouldStylize) {
                    try {
                        const lineDrawingDataURL = await mediaController.convertToLineDrawing(snapshotCanvas, { silent: true });
                        await recordFrameResult(lineDrawingDataURL);
                    } catch (error) {
                        console.warn('[VideoExport] 线稿转换失败，使用原始帧', error);
                        await recordFrameResult(snapshotCanvas.toDataURL());
                    }
                } else {
                    await recordFrameResult(snapshotCanvas.toDataURL());
                }
            };
        }
    
        return {
            enhancements: {
                ...(writeToVideo && captureCanvas ? { captureCanvas } : {}),
                frameProcessor: processFrame
            },
            cleanup: () => {
                captureCanvas?.remove();
                if (collectZip && capturedFrames.length) {
                    void downloadCapturedFramesAsZip(capturedFrames, zipFileName);
                }
            }
        };
    }

    async buildVideoFramePostProcessor(
        recordingCamera: RecordingCamera,
        options: VideoExportOptions
    ): Promise<FramePostProcessorSetup | null> {
        const factories: Array<() => Promise<FramePostProcessorSetup | null>> = [];

        var debugModeEnabled = Boolean((globalThis as any).GS_VIDEO_EXPORT_DEBUG);
        debugModeEnabled = false
        const shouldAttachLineDrawing = options.enableLineDrawing || debugModeEnabled;
        const shouldAttachExgs = options.enableExgs;

        if (shouldAttachExgs) {
            const writeToVideo = !!options.enableExgs;
            factories.push(() => this.createExgsPostProcessor(recordingCamera, {
                enableDebugger: debugModeEnabled,
                writeToVideo,
                collectZip: debugModeEnabled,
                zipFileName: writeToVideo ? 'exgs' : 'exgs-debug',
                targetResolution: options.exgsResolution ?? this.exgsDefaultResolution,
            }));
        }

        if (shouldAttachLineDrawing) {
            const writeToVideo = !!options.enableLineDrawing;
            factories.push(() => this.createLineDrawingPostProcessor(recordingCamera,{
                enableDebugger: debugModeEnabled,
                writeToVideo,
                collectZip: debugModeEnabled,
                zipFileName: writeToVideo ? 'line-drawing' : 'line-drawing-debug'
            }));
        }

        for (const factory of factories) {
            try {
                const result = await factory();
                if (result) {
                    return result;
                }
            } catch (error) {
                console.warn('[VideoExport] 初始化帧后处理失败:', error);
            }
        }

        return null;
    }
}

export const mediaController = new MediaController();